import { AnyUIProvider } from '@any-ui-react/core';
import { ModalsProvider } from '@any-ui-react/modals';
import { ApolloProvider } from '@apollo/client';
import type { MantineTheme } from '@mantine/core';
import { useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { RouterWrapper } from '@/common/components/shared/layout/routes/RouterWrapper';
import { MaterialIcons } from '@/common/components/shared/material-icons';
import { modalProps } from '@/common/components/shared/modal';
import { Toaster } from '@/common/components/shared/toaster';
import { AuthContext, CurrentEmployeeProvider } from '@/common/contexts';
import { apolloClient, getAuthUserInSession } from './utils';

import './styles/globals.css';
import type { CalendarStylesNames } from '@mantine/dates';

const fontFamily = [
  'Inter var',
  // 'ui-sans-serif',
  // 'system-ui',
  // '-apple-system',
  // 'BlinkMacSystemFont',
  // 'Segoe UI',
  // 'Segoe UI Emoji',
  // 'Segoe UI Symbol',
  // 'Apple Color Emoji',
  // 'Twemoji Mozilla',
  // 'Noto Color Emoji',
  // 'Android Emoji',
  'Roboto',
  'Helvetica Neue',
  'Arial',
  'Noto Sans',
  'sans-serif',
]
  .map(it => (/\s+/.test(it) ? `"${it}"` : it))
  .join(', ');

// nth-child(6) -> Saturday, nth-child(7) -> Sunday
const calendarDefaultProps: { classNames: Partial<Record<CalendarStylesNames, string>> } = {
  classNames: {
    monthThead: '[&_tr]:border-b',
    monthTbody: '[&_tr:first-child_td]:pt-[6px]',
    yearsListControl: '[&:disabled]:cursor-not-allowed',
    monthsListControl: '[&:disabled]:cursor-not-allowed',
    weekday: '[&:nth-child(6)]:text-blue-5 [&:nth-child(7)]:text-red-5',
    monthCell: `
    [&:nth-child(6)_.mantine-Calendar-day]:text-blue-5
    [&:nth-child(7)_.mantine-Calendar-day]:text-red-5
    [&:nth-child(6)_.mantine-DatePicker-day]:text-blue-5
    [&:nth-child(7)_.mantine-DatePicker-day]:text-red-5
    `
      .replace(/[\s]+/g, ' ')
      .trim(),
    day: '[&:disabled]:cursor-not-allowed',
  },
};

const components: MantineTheme['components'] = {
  Accordion: {
    defaultProps: {
      classNames: {
        item: '[&[data-active=true]]:bg-white',
      },
    },
  },
  ActionIcon: {
    defaultProps: {
      variant: 'transparent',
    },
  },
  Calendar: {
    defaultProps: {
      ...calendarDefaultProps,
    },
  },
  Checkbox: {
    defaultProps: {
      classNames: {
        root: '[&[data-checked=true]_.mantine-Checkbox-label]:text-blue-6',
      },
    },
  },
  DatePicker: {
    defaultProps: {
      ...calendarDefaultProps,
    },
  },
  Menu: {
    defaultProps: {
      classNames: {
        dropdown: 'shadow',
        item: 'hover:bg-gray-1',
        itemLabel: 'text-sm',
      },
    },
  },
  Modal: {
    defaultProps: {
      classNames: {
        header: 'px-6 py-4 border-b border-b-gray mb-4',
        body: 'px-6 pt-0 pb-8',
      },
    },
  },
  MultiSelect: {
    defaultProps: {
      rightSection: <MaterialIcons iconName="keyboard_arrow_down" className="cursor-pointer" />,
      rightSectionPointerEvents: 'none',
    },
  },
  Radio: {
    defaultProps: {
      classNames: {
        root: '[&[data-checked=true]_.mantine-Radio-label]:text-blue-6',
      },
    },
  },
  Select: {
    defaultProps: {
      rightSection: <MaterialIcons iconName="keyboard_arrow_down" className="cursor-pointer" />,
      rightSectionPointerEvents: 'none',
    },
  },
  Tabs: {
    defaultProps: {
      classNames: {
        list: '!min-h-12 flex-nowrap overflow-x-auto',
      },
      styles: {
        list: {
          scrollbarGutter: 'stable',
        },
      },
    },
  },
  Tooltip: {
    defaultProps: {
      classNames: {
        tooltip: 'text-xs',
      },
    },
  },
};

function App() {
  const [authUser, setAuthUser] = useState(getAuthUserInSession());

  return (
    <HelmetProvider>
      <Helmet>
        <title>AnyMind MED</title>
      </Helmet>
      <AnyUIProvider label={{ noOptions: 'No Option' }} theme={{ fontFamily, components }}>
        <ApolloProvider client={apolloClient}>
          <AuthContext.Provider value={{ authUser, setAuthUser }}>
            <CurrentEmployeeProvider>
              <ModalsProvider modalProps={modalProps}>
                <Toaster />
                <RouterWrapper />
              </ModalsProvider>
            </CurrentEmployeeProvider>
          </AuthContext.Provider>
        </ApolloProvider>
      </AnyUIProvider>
    </HelmetProvider>
  );
}

export default App;
